import React, { useEffect, useRef, useState } from 'react'
import {
  Checkbox,
  CheckboxLabel,
  Form,
  FormField,
  Input,
  Label,
  Select,
  TextArrayInput,
} from '@/components/ui-primitives/small'
import { TextTableInput } from '@/components/ui-primitives/small/text-table-input'
import { stableRefTo } from '@/utils/react-utils'
import { CollapsibleSection } from '@/components/editor/widget-editor/collapsible-section'
import { ViewerConfig } from '@/components/Viewer'
import { saveViewerSnapshot } from '@/components/editor/widget-editor/undo-redo'
import { LAYOUT_NAME, LayoutName } from '@/helpers/layout'
import { CodeEditor, JsonCodeEditor } from '@/components/editor/code-editor'
import { Alert, AlertDescription } from '@/components/ui-primitives/alert'
import { CircleAlert, Sparkle } from 'lucide-react'
import { FontListButton } from '@/components/FontList'
import { LabelWithHelp } from '@/components/FormField'
import { FormGroup } from '@/components/FormGroup'
import widgetConfigStyles from '@/components/WidgetConfiguration.module.css'
import { useLocalSettings } from '@/components/editor/local-settings'
import { useDebounce } from '@/hooks/use-debounce'

export function AppSettings(props: {
  viewerConfig: ViewerConfig,
  onChange: (viewer: ViewerConfig) => void,
  selectLayout: (layoutName: LayoutName) => ViewerConfig
}) {
  const [ draftConfig, setDraftConfig ] = useState(props.viewerConfig)
  const debounceTime = useLocalSettings(st => (st.liveUpdateDelay * 1000) ?? 200)
  const touchedFields = useRef<string[]>([])

  useEffect(() => {
    setDraftConfig(props.viewerConfig)
  }, [ props.viewerConfig ])

  useDebounce(() => {
    if (draftConfig === props.viewerConfig) return
    const description = `${[ ...touchedFields.current ].join(', ')} app field change`
    props.onChange(draftConfig)
    saveViewerSnapshot(draftConfig, description)
    touchedFields.current = []

  }, debounceTime, [ draftConfig ])

  const updateDraft = <K extends keyof ViewerConfig>(field: K, value: ViewerConfig[K]) => {
    const newViewer = { ...props.viewerConfig, [field]: value }
    setDraftConfig(newViewer)
    touchedFields.current.push(field)
  }

  return (
    <Form className="gap-0 !p-0">
      <CollapsibleSection header="General" id="app-general">
        <FormField id="title">
          <Label>Title</Label>
          <Input type="text"
                 value={draftConfig.title || ''}
                 onChange={e => updateDraft('title', e.currentTarget.value)} />
        </FormField>
        <FormField id="subtitle">
          <Label>Subtitle</Label>
          <Input type="text"
                 value={draftConfig.subtitle || ''}
                 onChange={e => updateDraft('subtitle', e.currentTarget.value)} />
        </FormField>
        <FormField id="infoMessage">
          <Label>Info message</Label>
          <Input type="text"
                 value={draftConfig.infoMessage || ''}
                 onChange={e => updateDraft('infoMessage', e.currentTarget.value)} />
        </FormField>
        <FormField id="popupInfoAtStart">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.popupInfoAtStart ?? false}
              onClick={() => updateDraft('popupInfoAtStart', !draftConfig.popupInfoAtStart)} />
            Show popup info at start
          </CheckboxLabel>
        </FormField>
        <FormField id="public">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.public ?? false}
              onClick={() => updateDraft('public', !draftConfig.public)} />
            Public (publiek)
          </CheckboxLabel>
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="SPARQL endpoint" id="app-endpoint" defaultExpanded>
        <FormField id="bbsparqlendpoint">
          <Label>Backend repo and app parameters</Label>
          {/[&?]login=.+[&]psw=.+|[&?]psw=.+[&]login=.+/i.test(draftConfig.bbsparqlendpoint || '') && (
            <Alert className="p-2 bg-yellow-100 text-yellow-900 !borderyellow-400">
              <AlertDescription className="flex gap-2 items-center text-xs">
                <CircleAlert size={14} className="min-w-[14px]" />
                <div className="flex-grow">
                  Deprecated auto-login method detected (&login=xxx&psw=xxx).{' '}
                  Please configure an anonymous app user instead.{' '}
                  See <a href="https://bim-connected.slite.com/app/docs/Wbh6CtS-ClmZDh#YkmqXuN5onfWDw"
                         className="underline"
                         target="_blank"
                         rel="noreferrer">this guide</a>.
                </div>
              </AlertDescription>
            </Alert>
          )}
          <Input type="text"
                 value={draftConfig.bbsparqlendpoint || ''}
                 onChange={e => updateDraft('bbsparqlendpoint', e.currentTarget.value)} />
        </FormField>
        <FormField id="repoURL">
          <Label>Sparql endpoint</Label>
          <Input type="text"
                 value={draftConfig.repoURL || ''}
                 onChange={e => updateDraft('repoURL', e.currentTarget.value)} />
        </FormField>
        <FormField id="anonymousLogin">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.anonymousLogin ?? false}
              onClick={() => updateDraft('anonymousLogin', !draftConfig.anonymousLogin)} />
            Enable anonymous login for this app. An anonymous user must be set up in User Management for this to work.
          </CheckboxLabel>
        </FormField>
        <FormField id="localRepoURL">
          <Label>Local SPARQL endpoint (http://localhost:7200/repositories/test)</Label>
          <Input type="text"
                 value={draftConfig.localRepoURL || ''}
                 onChange={e => updateDraft('localRepoURL', e.currentTarget.value)} />
        </FormField>
        <FormField id="sparqlErrorMessage">
          <Label>SPARQL endpoint error message</Label>
          <Input type="text"
                 value={draftConfig.sparqlErrorMessage || ''}
                 onChange={e => updateDraft('sparqlErrorMessage', e.currentTarget.value)} />
        </FormField>
        <FormGroup tag="fieldset" className={widgetConfigStyles.fieldset}>
          <legend>On a failing SPARQL query <New /></legend>
          <FormField id="strictSparqlErrors">
            <CheckboxLabel>
              <Checkbox
                checked={draftConfig.strictSparqlErrors ?? false}
                onClick={() => updateDraft('strictSparqlErrors', !draftConfig.strictSparqlErrors)} />
              Replace the widget with the error message above
            </CheckboxLabel>
          </FormField>
          <FormField id="notifySparqlErrors">
            <CheckboxLabel>
              <Checkbox
                checked={draftConfig.notifySparqlErrors ?? false}
                onClick={() => updateDraft('notifySparqlErrors', !draftConfig.notifySparqlErrors)} />
              Show a notification
            </CheckboxLabel>
          </FormField>
          <p className="text-xs text-muted-foreground mt-1">
            With neither enabled, a failed query is only logged to the developer
            console and the widget keeps rendering whatever data it had.
          </p>
        </FormGroup>
      </CollapsibleSection>
      <CollapsibleSection header="Default parameter values" id="app-variables" defaultExpanded>
        <FormField id="parameterValues">
          <CodeEditor
            name="parameterValues"
            mode="json"
            value={draftConfig.parameterValues || ''}
            onValueChange={value => updateDraft('parameterValues', value)}
          />
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="Layout and styling" id="app-layout">
        <FormField id="layout">
          <Label>Layout</Label>
          <Select value={props.viewerConfig.layoutName}
                  onChange={e => {
                    const newViewer = props.selectLayout(e.currentTarget.value as any)
                    saveViewerSnapshot(newViewer, 'layout change')
                  }}>
            <option value={LAYOUT_NAME.CUSTOM}>Custom</option>
            <option value={LAYOUT_NAME.ORIGINAL}>Original</option>
          </Select>
        </FormField>
        <FormField id="notShowTop">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.notShowTop ?? false}
              onClick={() => updateDraft('notShowTop', !draftConfig.notShowTop)} />
            Do not show top
          </CheckboxLabel>
        </FormField>
        <FormField id="logoutbutton">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.logoutbutton ?? false}
              onClick={() => updateDraft('logoutbutton', !draftConfig.logoutbutton)} />
            Show logout button in the header
          </CheckboxLabel>
        </FormField>
        <FormField id="notShowBottom">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.notShowBottom ?? false}
              onClick={() => updateDraft('notShowBottom', !draftConfig.notShowBottom)} />
            Do not show bottom
          </CheckboxLabel>
        </FormField>
        <FormField id="logoURL">
          <Label>Logo URL</Label>
          <Input type="text"
                 value={draftConfig.logoURL || ''}
                 onChange={e => updateDraft('logoURL', e.currentTarget.value)} />
        </FormField>
        <FormField id="css">
          <Label className="flex gap-2">
            <div className="flex-grow">
              Global CSS in JSON formaat, bijvoorbeeld: {'{"fontFamily": "Exo"}'}
            </div>
            <FontListButton />
          </Label>
          <Input type="text"
                 value={draftConfig.css || ''}
                 onChange={e => updateDraft('css', e.currentTarget.value)} />
        </FormField>
        <FormField id="pageWidth">
          <Label>Page width</Label>
          <Input type="text"
                 value={draftConfig.pageWidth || ''}
                 onChange={e => updateDraft('pageWidth', e.currentTarget.value)} />
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="URL parameters" id="app-url-params">
        <FormField id="updateURLVariables">
          <LabelWithHelp
            label={<>App-variables to encode in the URL as an <b>update</b> in the browser's history</>}
            helpComponent={InfoUpdateURLAppVars}
          />
          <TextArrayInput
            value={draftConfig.updateURLVariables}
            onChange={value => updateDraft('updateURLVariables', value)} />
        </FormField>
        <FormField id="pushURLVariables">
          <LabelWithHelp
            label={<>App-variables to encode in the URL as a <b>push</b> in the browser's history</>}
            helpComponent={InfoPushURLAppVars}
          />
          <TextArrayInput
            value={draftConfig.pushURLVariables}
            onChange={value => updateDraft('pushURLVariables', value)} />
        </FormField>
        <FormField id="parseUrlProperties">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.parseUrlProperties ?? false}
              onClick={() => updateDraft('parseUrlProperties', !draftConfig.parseUrlProperties)} />
            Parse URL parameters as property values
          </CheckboxLabel>
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="Refresh and caching" id="app-caching">
        <FormField id="update60Enabled">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.update60Enabled ?? false}
              onClick={() => updateDraft('update60Enabled', !draftConfig.update60Enabled)} />
            Enable update60 parameter refresh at 60s interval
          </CheckboxLabel>
        </FormField>
        <FormField id="refetchOnWindowFocus">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.refetchOnWindowFocus ?? false}
              onClick={() => updateDraft('refetchOnWindowFocus', !draftConfig.refetchOnWindowFocus)} />
            Refresh SPARQL when returning to this tab or window <New />
          </CheckboxLabel>
        </FormField>
        <FormField id="refetchOnReconnect">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.refetchOnReconnect ?? false}
              onClick={() => updateDraft('refetchOnReconnect', !draftConfig.refetchOnReconnect)} />
            Refresh SPARQL when the network connection is restored <New />
          </CheckboxLabel>
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="Localization" id="app-localization">
        <FormField id="localizeDateTime">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.localizeDateTime ?? false}
              onClick={() => updateDraft('localizeDateTime', !draftConfig.localizeDateTime)} />
            Show dateTimes in the local timezone (only implemented for table++)
          </CheckboxLabel>
        </FormField>
        <FormField id="dateTimeLocalizationFormat">
          <LabelWithHelp
            label={<>Provide the dateTime format you would like to use.</>}
            helpComponent={InfoDateTimeLocalizationFormat}
          />
          <Input type="text"
                 value={draftConfig.dateTimeLocalizationFormat ?? 'yyyy-MM-dd HH:mm'}
                 onChange={e => updateDraft('dateTimeLocalizationFormat', e.currentTarget.value)} />
        </FormField>
        <FormField id="useCustomBooleanLables">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.useCustomBooleanLables ?? false}
              onClick={() => updateDraft('useCustomBooleanLables', !draftConfig.useCustomBooleanLables)} />
            Use custom presentation for Booleans
          </CheckboxLabel>
        </FormField>
        <FormField id="booleanLabelMapping">
          <LabelWithHelp
            label={<>Provide a comma seperated list that defines how you want booleans to appear.</>}
            helpComponent={InfoBooleanLabelMapping}
          />
          <TextArrayInput
            value={draftConfig.booleanLabelMapping}
            onChange={value => updateDraft('booleanLabelMapping', value)} />
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="As component" id="app-as-component">
        <FormField id="componentInputVarNames">
          <Label>Input variables</Label>
          <TextArrayInput
            variant="bottom-add-button"
            value={draftConfig.componentInputVarNames ?? []}
            onChange={value => updateDraft('componentInputVarNames', value)}
          />
        </FormField>
        <FormField id="componentOutputVarNames">
          <Label>Output variables</Label>
          <TextArrayInput
            variant="bottom-add-button"
            value={draftConfig.componentOutputVarNames ?? []}
            onChange={value => updateDraft('componentOutputVarNames', value)}
          />
        </FormField>
        <FormField id="componentDebugDefaultVars">
          <Label>Default app-variables when not used as component</Label>
          <TextTableInput
            columnLabels={stableRefTo(['name', 'value'], 'app-as-component-debug-default-vars')}
            valueOnlyAsInitial
            value={
              Object.keys(draftConfig.componentDebugDefaultVars ?? {}).length
                ? Object.entries(draftConfig.componentDebugDefaultVars as Record<string, string>)
                : Array.from({ length: 3 }).map(() => ['', ''])
            }
            onChange={rows => {
              const vars: ViewerConfig['componentDebugDefaultVars'] = {}
              for (const [key, value] of rows) {
                if (!key) continue
                vars[key] = value
              }
              updateDraft('componentDebugDefaultVars', vars)
            }}
          />
        </FormField>
      </CollapsibleSection>
      <CollapsibleSection header="Advanced" id="app-advanced">
        <FormField id="sseEnabled">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.sseEnabled ?? false}
              onClick={() => updateDraft('sseEnabled', !draftConfig.sseEnabled)} />
            <span>Enable server-side events (required to use <code>event.</code> variables). Requires a Wistor backend</span>
          </CheckboxLabel>
        </FormField>
        <FormField id="displayChromiumTranslateErrors">
          <CheckboxLabel>
            <Checkbox
              checked={draftConfig.displayChromiumTranslateErrors ?? false}
              onClick={() => updateDraft('displayChromiumTranslateErrors', !draftConfig.displayChromiumTranslateErrors)} />
            Allow widget errors that might come from Chromium translate
          </CheckboxLabel>
          <p className="text-xs text-muted-foreground mt-1">
            By default we do not display widget-errors that might come from{' '}
            <a className="text-blue-500 underline"
               href="https://gitlab.com/bim-connected/bim-bridge/linked-data-view-configurator/-/work_items/453">
              faulty interaction between Chromium translate and Wistor
            </a>.
            {' '}It can be useful to enable this option, if your application is acting weird.
          </p>
        </FormField>
        <FormField id="prejs">
          <Label>Pre-app JavaScript</Label>
          <CodeEditor
            name="prejs"
            mode="javascript"
            value={draftConfig.prejs || ''}
            onValueChange={value => updateDraft('prejs', value)}
          />
        </FormField>
        <FormField id="config-json">
          <Label>Config in JSON format</Label>
          <JsonCodeEditor
            name="appConfig"
            value={props.viewerConfig}
            onValueChange={value => {
              if (!value) return
              // DEBT: this could introduce a wrongly typed viewer
              props.onChange(value as any)
              saveViewerSnapshot(value as any, 'viewer JSON update')
            }}
            publishValueChangeOnBlur
          />
        </FormField>
      </CollapsibleSection>
    </Form>
  )
}

function New() {
  return (
    <sup className="inline-flex gap-0.5 whitespace-nowrap items-center text-wistor-alt">
      <Sparkle size={8} />
      <i>New!</i>
    </sup>
  )
}

function InfoUpdateURLAppVars() {
  return <>
    Changes in app-variables that are configured here will overwrite the current browser location.
    <i>The browser will be unable to go back in history to the previous set of app-variables.</i>
  </>
}

function InfoPushURLAppVars() {
  return <>
    Changes in app-variables that are configured here will push the current browser location.
    <i>The browser will be able to go back in history to the previous set of app-variables.</i>
  </>
}

function InfoDateTimeLocalizationFormat() {
  return <>
    Provide the dateTime format you would like to use. It uses date-fns formatting tokens<br/>
    Examples:<br/>
    yyyy-MM-dd HH:mm → 2024-01-01 14:30<br/>
    dd-MM-yyyy → 01-01-2024<br/>
    HH:mm:ss → 14:30:45<br/>
    EEEE, dd MMMM yyyy → Monday, 01 January 2024<br/>
    yyyy-MM-dd'T'HH:mm:ss.SSSXXX → 2024-01-01T12:00:00.000+00:00
  </>
}

function InfoBooleanLabelMapping() {
  return <>
    Provide a comma seperated list that define how you want booleans to appear,
    eg: "trueText, falseText, emptyText".<br/>
    <i>It will <b>only</b> work if 2 or 3 values are provided.</i><br/>
    If 2 values are given, meaning no value if given for emptyText, then emptyText will default to an empty string<br/>
    Note that the emptyText label is only used in places where the value is set, eg: BBDForm,
    in places where it is viewed such as table++ it will show up as empty
  </>
}
